import {
  useRef, forwardRef, useImperativeHandle,
  useState, useCallback, useEffect,
} from "react";
import { motion, AnimatePresence, useMotionValue, useSpring } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "@studio-freight/lenis";

gsap.registerPlugin(ScrollTrigger);

/* ─── scroll lock ─────────────────────────────────────────── */
function lockScroll() {
  document.documentElement.style.overflow = "hidden";
  window.dispatchEvent(new Event("overlay:open"));
}
function unlockScroll() {
  document.documentElement.style.overflow = "";
  window.dispatchEvent(new Event("overlay:close"));
}

/* ─── iris geometry ───────────────────────────────────────── */
function irisOrigin(rect) {
  return {
    x: +(((rect.left + rect.width / 2) / window.innerWidth) * 100).toFixed(2),
    y: +(((rect.top + rect.height / 2) / window.innerHeight) * 100).toFixed(2),
  };
}
function irisRadius(rect) {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const W = window.innerWidth, H = window.innerHeight;
  return Math.ceil(Math.hypot(Math.max(cx, W - cx), Math.max(cy, H - cy)));
}

/* ─── timing ──────────────────────────────────────────────── */
// Keep unlock delay > iris duration so scroll never re-appears mid-animation
const IRIS_DURATION = 0.68;
const UNLOCK_DELAY = Math.round(IRIS_DURATION * 1000) + 120; // extra buffer for slow mobile
const IRIS_T = { duration: IRIS_DURATION, ease: [0.76, 0, 0.24, 1] };

const backIn = {
  hidden: { opacity: 0, y: -8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1], delay: 0.5 } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.18 } },
};

/* ─── magnetic button hook ───────────────────────────────── */
function useMagnetic(strength = 0.35) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 18 });
  const sy = useSpring(y, { stiffness: 200, damping: 18 });

  const onMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  }, [x, y, strength]);

  const onLeave = useCallback(() => { x.set(0); y.set(0); }, [x, y]);

  return { ref, sx, sy, onMove, onLeave };
}

/* ═══════════════════════════════════════════════════════════ */
export default forwardRef(function TransitionOverlay(_, ref) {
  const [item, setItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const [radius, setRadius] = useState(0);

  // Stable ref so doClose always collapses to the correct origin
  const originRef = useRef({ x: 50, y: 50 });
  // Frozen snapshot used for the exit clip — must not change once close starts
  const exitOriginRef = useRef({ x: 50, y: 50 });
  const closingRef = useRef(false);
  const scrollElRef = useRef(null);
  const lenisRef = useRef(null);
  const lenisTickerRef = useRef(null);

  const startLenis = useCallback(() => {
    if (lenisRef.current) return;
    const el = scrollElRef.current;
    if (!el) return;
    const l = new Lenis({
      wrapper: el,
      content: el.firstChild,
      duration: 1.1,
      easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: true,   // smooth on mobile too
      touchMultiplier: 1.6,
    });
    const tick = (t) => l.raf(t * 1000);
    lenisTickerRef.current = tick;
    gsap.ticker.add(tick);
    l.on("scroll", ScrollTrigger.update);
    lenisRef.current = l;
  }, []);

  const stopLenis = useCallback(() => {
    if (!lenisRef.current) return;
    if (lenisTickerRef.current) {
      gsap.ticker.remove(lenisTickerRef.current);
      lenisTickerRef.current = null;
    }
    lenisRef.current.destroy();
    lenisRef.current = null;
  }, []);

  useImperativeHandle(ref, () => ({
    open({ rect, item: newItem }) {
      if (closingRef.current) return;
      const o = irisOrigin(rect);
      originRef.current = o;
      exitOriginRef.current = o; // keep exit in sync while open
      setOrigin(o);
      setRadius(irisRadius(rect));
      setItem(newItem);
      setOpen(true);
      lockScroll();
    },
    close: () => doClose(),
  }));

  const doClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    stopLenis();
    // Freeze exit origin BEFORE unmounting so AnimatePresence exit uses the right value
    exitOriginRef.current = originRef.current;
    setOpen(false);
    // Unlock only after iris exit animation is fully done
    setTimeout(() => {
      unlockScroll();
      setItem(null);
      closingRef.current = false;
    }, UNLOCK_DELAY);
  }, [stopLenis]);

  // Escape key
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") doClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, doClose]);

  // Start Lenis after the overlay is visible; reset scroll position first
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      if (scrollElRef.current) scrollElRef.current.scrollTop = 0;
      startLenis();
    }, 120); // slightly longer — ensures DOM is fully painted on slow mobile
    return () => clearTimeout(id);
  }, [open, item, startLenis]);

  const ox = origin.x, oy = origin.y;
  const exOx = exitOriginRef.current.x, exOy = exitOriginRef.current.y;
  const openClip = `circle(${radius}px at ${ox}% ${oy}%)`;
  const closedClip = `circle(0px at ${ox}% ${oy}%)`;
  const exitClip = `circle(0px at ${exOx}% ${exOy}%)`;

  return (
    <AnimatePresence>
      {open && item && (
        <motion.div
          key={`iris-${item.slug}`}
          initial={{ clipPath: closedClip }}
          animate={{ clipPath: openClip }}
          exit={{ clipPath: exitClip }}
          transition={IRIS_T}
          style={{
            position: "fixed", inset: 0, zIndex: 9998,
            // No overflow:hidden here — double-clipping causes compositor glitches on mobile
            // clip-path already masks everything outside the circle
            transform: "translateZ(0)", // promote layer without willChange churn
          }}
        >
          {/* scroll container */}
          <div
            ref={scrollElRef}
            className="overlay-scroll"
            style={{
              position: "absolute", inset: 0,
              overflowY: "auto", overflowX: "hidden",
              // WebkitOverflowScrolling: touch is deprecated and conflicts with scroll-lock on Samsung Browser
              background: "#0D0D0B",
            }}
          >
            <div>
              <OverlayContent item={item} lenisRef={lenisRef} open={open} scrollElRef={scrollElRef} />
            </div>
          </div>

          <BackButton onClick={doClose} variants={backIn} />
        </motion.div>
      )}
    </AnimatePresence>
  );
});

/* ─── Back button ─────────────────────────────────────────── */
function BackButton({ onClick, variants }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.button
      variants={variants}
      initial="hidden"
      animate="show"
      exit="exit"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="font-Montreal-Mono"
      style={{
        position: "absolute",
        top: "clamp(20px,2.8vw,32px)",
        left: "clamp(20px,3.5vw,44px)",
        zIndex: 20,
        background: hovered ? "rgba(245,244,240,0.96)" : "rgba(13,13,11,0.72)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid rgba(245,244,240,0.16)",
        color: hovered ? "#0D0D0B" : "#F5F4F0",
        padding: "10px 20px",
        borderRadius: 999,
        fontSize: 10,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 8,
        transition: "background 0.22s ease, color 0.22s ease",
      }}
    >
      <motion.span
        animate={{ x: hovered ? -2 : 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{ display: "inline-block", lineHeight: 1 }}
      >
        ←
      </motion.span>
      Back
    </motion.button>
  );
}

/* ═══════════════════════════════════════════════════════════
   OverlayContent
═══════════════════════════════════════════════════════════ */
function OverlayContent({ item, lenisRef, open, scrollElRef }) {
  const rootRef = useRef(null);
  const eyebrowRef = useRef(null);
  const titleRef = useRef(null);
  const metaRef = useRef(null);
  const descRef = useRef(null);
  const imgRef = useRef(null);
  const progressRef = useRef(null);
  const gsapCtx = useRef(null);

  useEffect(() => {
    if (!open || !rootRef.current) return;

    const init = setTimeout(() => {
      const lenis = lenisRef.current;
      const scroller = lenis?.wrapper ?? scrollElRef.current;
      if (!scroller) return;

      gsapCtx.current = gsap.context(() => {

        /* progress bar */
        if (progressRef.current) {
          gsap.to(progressRef.current, {
            scaleX: 1, ease: "none",
            scrollTrigger: {
              scroller, trigger: rootRef.current,
              start: "top top", end: "bottom bottom", scrub: 0.4,
            },
          });
        }

        /* eyebrow */
        if (eyebrowRef.current) {
          gsap.fromTo(eyebrowRef.current,
            { opacity: 0, y: -12 },
            {
              opacity: 1, y: 0, duration: 0.6, ease: "power3.out",
              scrollTrigger: { scroller, trigger: eyebrowRef.current, start: "top 95%", once: true },
            }
          );
        }

        /* title chars */
        if (titleRef.current) {
          const chars = titleRef.current.querySelectorAll(".char");
          gsap.fromTo(chars,
            { y: "110%", opacity: 0 },
            {
              y: "0%", opacity: 1,
              duration: 1, ease: "power4.out", stagger: 0.016,
              scrollTrigger: { scroller, trigger: titleRef.current, start: "top 90%", once: true },
            }
          );
        }

        /* meta + desc + image */
        [metaRef, descRef, imgRef].forEach((r, i) => {
          if (!r.current) return;
          gsap.fromTo(r.current,
            { opacity: 0, y: 36 },
            {
              opacity: 1, y: 0, duration: 0.8, ease: "power3.out",
              delay: i * 0.04,
              scrollTrigger: { scroller, trigger: r.current, start: "top 88%", once: true },
            }
          );
        });

      }, rootRef.current);
    }, 120);

    return () => { clearTimeout(init); gsapCtx.current?.revert(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  if (!item) return null;

  const paras = item.description?.trim().split("\n\n") ?? [];
  const titleChars = item.title.split("").map((ch, i) => (
    <span key={i} className="char" style={{ display: "inline-block", overflow: "hidden", whiteSpace: "pre", marginRight: ch === " " ? "0.18em" : 0 }}>
      {ch}
    </span>
  ));

  return (
    <div ref={rootRef} style={{ color: "#F5F4F0", minHeight: "100vh" }}>

      {/* scroll progress bar */}
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0,
        height: 2, zIndex: 30, pointerEvents: "none",
        background: "rgba(245,244,240,0.05)",
      }}>
        <div ref={progressRef} style={{
          height: "100%",
          background: "linear-gradient(to right, rgba(245,244,240,0.6), rgba(245,244,240,0.2))",
          transformOrigin: "left center",
          transform: "scaleX(0)",
        }} />
      </div>

      <div style={{
        maxWidth: 1100,
        margin: "0 auto",
        padding: "clamp(88px,11vw,150px) clamp(24px,6vw,80px) clamp(64px,8vw,100px)",
      }}>

        {/* Eyebrow */}
        <div
          ref={eyebrowRef}
          style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "clamp(24px,3.5vw,44px)",
            opacity: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{
              width: 7, height: 7, borderRadius: "50%",
              background: item.bg || "rgba(245,244,240,0.3)",
              display: "inline-block", flexShrink: 0,
            }} />
            <span className="font-Montreal-Mono" style={{
              fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase",
              color: "rgba(245,244,240,0.35)",
            }}>
              {item.role}
            </span>
            <span style={{ width: 20, height: 1, background: "rgba(245,244,240,0.12)" }} />
            <span className="font-Montreal-Mono" style={{
              fontSize: 10, letterSpacing: "0.22em",
              color: "rgba(245,244,240,0.2)",
            }}>
              {item.year}
            </span>
          </div>
          <span className="font-Montreal-Mono" style={{
            fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase",
            color: "rgba(245,244,240,0.12)",
          }}>
            Case Study
          </span>
        </div>

        {/* Title */}
        <h1
          ref={titleRef}
          className="font-Montreal-Bold uppercase"
          style={{
            fontSize: "clamp(2.4rem,6.5vw,4.5vw)",
            letterSpacing: "-0.04em", lineHeight: 0.9,
            color: "#F5F4F0",
            marginBottom: "clamp(28px,4vw,52px)",
            overflow: "hidden",
          }}
        >
          {titleChars}
        </h1>

        {/* Tags + Visit */}
        <div
          ref={metaRef}
          style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap", gap: 14,
            marginBottom: "clamp(40px,6vw,80px)",
            opacity: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {item.tags.map((tag, i) => <TagPill key={i} label={tag} />)}
          </div>
          {item.url && <VisitButton url={item.url} />}
        </div>

        {/* Divider */}
        <div style={{
          height: 1,
          background: "rgba(245,244,240,0.07)",
          marginBottom: "clamp(40px,6vw,80px)",
        }} />

        {/* Description */}
        <div ref={descRef} style={{ opacity: 0, marginBottom: "clamp(64px,9vw,110px)" }}>
          <p className="font-Montreal-Mono" style={{
            fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase",
            color: "rgba(245,244,240,0.18)", marginBottom: 22,
          }}>
            Overview
          </p>
          {paras.map((para, i) => (
            <p key={i} className="font-Montreal-Regular" style={{
              fontSize: "clamp(1rem,1.45vw,1.16rem)",
              lineHeight: 1.92,
              color: "rgba(245,244,240,0.58)",
              maxWidth: 700,
              marginBottom: i < paras.length - 1 ? "1.4em" : 0,
            }}>
              {para}
            </p>
          ))}

          {item.details?.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
              gap: "clamp(20px,3vw,36px)",
              marginTop: "clamp(36px,5vw,60px)",
              paddingTop: "clamp(28px,4vw,44px)",
              borderTop: "1px solid rgba(245,244,240,0.06)",
            }}>
              {item.details.map((d, i) => (
                <DetailCard key={i} label={d.label} body={d.body} />
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Image card */}
      <div
        ref={imgRef}
        style={{
          opacity: 0,
          padding: "0 clamp(16px,4vw,52px)",
          marginBottom: "clamp(80px,10vw,120px)",
        }}
      >
        <ImageCard item={item} />
      </div>

    </div>
  );
}

/* ─── Tag pill ────────────────────────────────────────────── */
function TagPill({ label }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.span
      className="font-Montreal-Mono"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      animate={{
        y: hovered ? -2 : 0,
        borderColor: hovered ? "rgba(245,244,240,0.32)" : "rgba(245,244,240,0.12)",
        color: hovered ? "rgba(245,244,240,0.72)" : "rgba(245,244,240,0.38)",
      }}
      transition={{ duration: 0.18, ease: "easeOut" }}
      style={{
        fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
        border: "1px solid rgba(245,244,240,0.12)",
        color: "rgba(245,244,240,0.38)",
        borderRadius: 999, padding: "5px 13px",
        cursor: "default", display: "inline-block",
      }}
    >
      {label}
    </motion.span>
  );
}

/* ─── Visit button ────────────────────────────────────────── */
function VisitButton({ url }) {
  const [hovered, setHovered] = useState(false);
  const mag = useMagnetic(0.28);
  return (
    <motion.a
      ref={mag.ref}
      href={url}
      target="_blank"
      rel="noreferrer"
      className="font-Montreal-Mono"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); mag.onLeave(); }}
      onMouseMove={mag.onMove}
      style={{
        x: mag.sx, y: mag.sy,
        display: "inline-flex", alignItems: "center", gap: 7,
        fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
        color: hovered ? "#0D0D0B" : "#F5F4F0",
        background: hovered ? "#F5F4F0" : "transparent",
        border: "1px solid rgba(245,244,240,0.3)",
        padding: "11px 24px",
        borderRadius: 999,
        textDecoration: "none",
        flexShrink: 0,
        transition: "background 0.22s ease, color 0.22s ease, border-color 0.22s ease",
      }}
    >
      Visit
      <motion.span
        animate={{ x: hovered ? 3 : 0, y: hovered ? -3 : 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{ display: "inline-block", lineHeight: 1 }}
      >
        ↗
      </motion.span>
    </motion.a>
  );
}

/* ─── Detail card ─────────────────────────────────────────── */
function DetailCard({ label, body }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      animate={{ borderLeftColor: hovered ? "rgba(245,244,240,0.28)" : "rgba(245,244,240,0.0)" }}
      transition={{ duration: 0.22 }}
      style={{ paddingLeft: 16, borderLeft: "2px solid rgba(245,244,240,0.0)" }}
    >
      <p className="font-Montreal-Mono" style={{
        fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase",
        color: hovered ? "rgba(245,244,240,0.32)" : "rgba(245,244,240,0.2)",
        marginBottom: 10,
        transition: "color 0.2s ease",
      }}>
        {label}
      </p>
      <p className="font-Montreal-Regular" style={{
        fontSize: "clamp(0.85rem,1.05vw,0.94rem)",
        lineHeight: 1.8,
        color: hovered ? "rgba(245,244,240,0.6)" : "rgba(245,244,240,0.46)",
        transition: "color 0.2s ease",
      }}>
        {body}
      </p>
    </motion.div>
  );
}

/* ─── Image card ──────────────────────────────────────────── */
function ImageCard({ item }) {
  const [hovered, setHovered] = useState(false);
  const mag = useMagnetic(0.06);

  return (
    <motion.div
      ref={mag.ref}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); mag.onLeave(); }}
      onMouseMove={mag.onMove}
      style={{
        x: mag.sx, y: mag.sy,
        borderRadius: "clamp(12px,1.6vw,20px)",
        overflow: "hidden",
        position: "relative",
        background: item.bg || "#111",
        boxShadow: hovered
          ? "0 0 0 1px rgba(245,244,240,0.14), 0 24px 80px rgba(0,0,0,0.55)"
          : "0 0 0 1px rgba(245,244,240,0.07), 0 8px 32px rgba(0,0,0,0.3)",
        transition: "box-shadow 0.35s ease",
        cursor: item.url ? "pointer" : "default",
      }}
      animate={{ scale: hovered ? 1.008 : 1 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      onClick={() => item.url && window.open(item.url, "_blank", "noreferrer")}
    >
      <img
        src={item.imgSrc}
        alt={`${item.title} preview`}
        style={{
          width: "100%", display: "block",
          objectFit: "cover", objectPosition: "top center",
          maxHeight: "80vh",
          transform: hovered ? "scale(1.03)" : "scale(1)",
          transition: "transform 0.65s cubic-bezier(0.22,1,0.36,1)",
        }}
      />

      <motion.div
        animate={{ opacity: hovered ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.28)",
          pointerEvents: "none",
        }}
      >
        <motion.div
          animate={{ scale: hovered ? 1 : 0.88, y: hovered ? 0 : 6 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="font-Montreal-Mono"
          style={{
            background: "rgba(245,244,240,0.96)",
            color: "#0D0D0B",
            padding: "12px 28px",
            borderRadius: 999,
            fontSize: 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          Open Project ↗
        </motion.div>
      </motion.div>

      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        padding: "clamp(14px,2vw,22px) clamp(20px,3vw,32px)",
        background: "linear-gradient(to top, rgba(0,0,0,0.6), transparent)",
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
      }}>
        <span className="font-Montreal-Mono" style={{
          fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase",
          color: "rgba(245,244,240,0.4)",
        }}>
          {item.title}
        </span>
        <span className="font-Montreal-Mono" style={{
          fontSize: 9, letterSpacing: "0.22em",
          color: "rgba(245,244,240,0.22)",
        }}>
          {item.year}
        </span>
      </div>
    </motion.div>
  );
}
